import { CircularProgress } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import { Autocomplete } from '@material-ui/lab';
import { RenderInputParams } from '@material-ui/lab/Autocomplete';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import gql from 'graphql-tag';
import * as React from 'react';
import { graphql } from 'react-apollo';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { EnquiryData, EnquiryOutcomeReason } from '../models/Enquiry';

interface SelectorProps {
    onSelection?: (selection: IAutoCompleteItem, name: string, reasonGuid: string) => void;
    // tslint:disable-next-line: no-any
    items?: any;
    required?: boolean;
    label?: string;
    name: string;
    data: EnquiryData;
    value: string; // GuidId
    disabled?: boolean;
    error?: boolean;
    className?: string;
    disablePortal?: boolean;
    disableCloseOnSelect?: boolean;
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
    helperText?: string;
    variant?: 'standard' | 'outlined' | 'filled';
    size?: 'small' | 'medium';
}

interface SelectorState {
    selectedCode: string;
    selectedLabel: string;
    error?: boolean;
}

// PureComponent skips re-rendering when none of its own props changed by shallow comparison -
// note this only helps where the caller's props are themselves stable; a caller passing a new
// inline arrow function for onSelection/onBlur on every render defeats it regardless (that prop
// always reads as "changed"). See each call site's own props if this doesn't visibly reduce
// re-renders where it's used.
class Selector extends React.PureComponent<SelectorProps, SelectorState> {
    // tslint:disable-next-line: no-any
    public reason: any;

    constructor(props: SelectorProps, state?: SelectorState) {
        super(props, state);
        
        this.handleChange = this.handleChange.bind(this);
        
        this.state = {
            selectedCode: '',
            selectedLabel: '',
            error: false
        };
    }

    public UNSAFE_componentWillReceiveProps(nextProps: SelectorProps) {
        if ( (this.props.value !== nextProps.value && this.reason) || 
                this.props.error !== nextProps.error ) {
            
            if (this.reason) {
                // tslint:disable-next-line: no-any
                const filteredItem = this.reason.find((item: any) => {
                    return item.value.toString().includes(nextProps.value.toString());
                });

                if (filteredItem && nextProps.value.toString() !== '') {
                    this.setState({
                        selectedCode: filteredItem.value,
                        selectedLabel: filteredItem.label
                    });
                } else {
                    this.setState({
                        selectedCode: '',
                        selectedLabel: ''
                    });
                }     
            }
            
            if (this.props.error !== nextProps.error) {
                this.setState({
                    error: nextProps.error
                });
            }
        }
    }

    public render() {
        if (!this.props.data.loading || this.props.data.enquiry) {
            const enquiryData: EnquiryData =  this.props.data;
            this.reason = enquiryData.enquiry.reasons.map( (source: EnquiryOutcomeReason) => ({
                value: source.description, 
                label: source.description, 
                reasonGuid: source.guidId, 
                isReferrer: source.isReferrer
            }));
        } else { 
            this.reason = [];
        }
        
        return (
            <div className={'downshift-wrapper'}>
                <Autocomplete
                    loading={this.props.data.loading}
                    disabled={this.props.disabled}
                    autoSelect={true}
                    // Explicit bug report: a fresh {value,label} object literal every render is
                    // never === any entry in this.reason - MUI's own "is value in options" check
                    // defaults to reference equality, so it logged its "invalid Autocomplete
                    // value" warning (full component-stack trace included) on every render,
                    // whether or not a real reason was selected. getOptionSelected replaces that
                    // default with a real value-based comparison (MUI's own documented fix - the
                    // warning text names this exact prop); null still covers "nothing selected".
                    getOptionSelected={(option, value) => option.value === value.value}
                    value={this.state.selectedCode ? { value: this.state.selectedCode, label: this.state.selectedLabel } : null}
                    onChange={this.handleChange}
                    options={this.reason}
                    disableCloseOnSelect={this.props.disableCloseOnSelect}
                    // tslint:disable-next-line: max-line-length
                    disablePortal={this.props.disablePortal !== undefined ? this.props.disablePortal : true} // to Stop the dropdown select from closing the form
                    // tslint:disable-next-line: jsx-no-lambda
                    getOptionLabel={option => option.label}   
                    onBlur={this.props.onBlur}                
                    // tslint:disable-next-line: jsx-no-lambda
                    renderInput={params => (
                        <TextField
                            {...params}
                            label={this.props.label}
                            variant={this.props.variant ? this.props.variant : 'standard'}
                            size={this.props.size ? this.props.size : 'medium'}
                            fullWidth={true}
                            margin="none"
                            required={this.props.required}
                            InputProps={this.getInputProps(params, this.props.data.loading)}
                            error={this.state.error}     
                            helperText={this.state.error ? this.props.helperText || 'This field is required' : ''}
                        />
                    )}
                    // tslint:disable-next-line: jsx-no-lambda
                    renderOption={(option, { inputValue }) => {
                        const matches = match(option.label, inputValue);
                        const parts = parse(option.label, matches);
                
                        return (
                          <div>
                            {parts.map((part, index) => (
                              <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                {part.text}
                              </span>
                            ))}
                          </div>
                        );
                      }}
                />
            </div>
        );
    }

    private getInputProps(params: RenderInputParams, isLoading?: boolean) {
        return  ({   
            ...params.InputProps,
            endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),     
         });
    }

    // tslint:disable-next-line: no-any
    private handleChange(event: object, item: any) {      
        
        // console.log('item', item);

        this.setState( {
            selectedCode: item ? item.value : '',
            selectedLabel: item ? item.label : ''
        });

        if (this.props.onSelection) {
            this.props.onSelection(
                {
                    label: item ? item.label : '', 
                    value: item ? item.value : '',
                    secondaryValue: item ? item.isReferrer : false
                }, 
                this.props.name,
                item ? item.reasonGuid : '');
        }      
    }
}

const EnquiryOutcomeReasonData = gql`
query enquiryReason {
	enquiry{
        reasons{
            guidId,
            description,
            isReferrer
        }
    }
}`;

// tslint:disable-next-line: no-any
export const ReasonSelector = graphql<any, SelectorProps, any>
    (EnquiryOutcomeReasonData, {
        options: () => ({  
            fetchPolicy: 'cache-first',
            variables: {}
        })
    })
    (Selector);